import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClient, onlineManager } from '@tanstack/react-query';
import type { PersistedClient } from '@tanstack/react-query-persist-client';

import { OFFLINE_GC_TIME_MS } from '@splitfiesta/core';

import {
  checkIsOnline,
  markOffline,
  setActualOnlineStatus,
  setOnlineManagerCallback,
} from '@/hooks/useNetworkStatus';

import { RetryManager } from '@/lib/errors/retryManager';

import { type ApplicationError, ErrorCategory } from '@/types/errors';

/**
 * Configure React Query's onlineManager to sync with actual connectivity
 * Enhanced to handle VPN scenarios where navigator.onLine is unreliable
 *
 * Instead of relying on navigator.onLine:
 * 1. We use a custom online check function that returns actual connectivity
 * 2. Network errors in queries/mutations will mark us as offline
 * 3. Successful requests will mark us as online
 */
if (typeof window !== 'undefined') {
  // Override the default online check to use our enhanced detection
  onlineManager.setEventListener(setOnline => {
    // Initial state from our enhanced detection
    setOnline(checkIsOnline());

    // Register callback to be notified immediately when status changes
    // This eliminates the polling delay that caused slow offline detection
    setOnlineManagerCallback(setOnline);

    // Listen to browser events but verify with actual connectivity
    const handleOnline = () => {
      // Don't immediately trust navigator.onLine - let our system verify
      // The useNetworkStatus hook will call checkActualConnectivity()
    };

    const handleOffline = () => {
      // Browser says offline - trust it and update
      setActualOnlineStatus(false);
      setOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  });
}

/**
 * Detect if an error is a network connectivity error
 */
function isNetworkError(error: unknown): boolean {
  if (!error) return false;

  // Check for fetch/network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }

  // Check for AbortError (timeouts)
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }

  // Check ApplicationError category
  if (isApplicationError(error) && error.category === ErrorCategory.NETWORK) {
    return true;
  }

  // Check for common network error messages
  const errorMessage = String((error as { message?: string })?.message || '');
  const networkErrorPatterns = [
    'network',
    'fetch',
    'connection',
    'timeout',
    'offline',
    'ECONNREFUSED',
    'ENOTFOUND',
    'ERR_NETWORK',
  ];

  return networkErrorPatterns.some(pattern =>
    errorMessage.toLowerCase().includes(pattern.toLowerCase())
  );
}

/**
 * Type guard to check if error is an ApplicationError
 */
function isApplicationError(error: unknown): error is ApplicationError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'category' in error &&
    'severity' in error
  );
}

/**
 * Enhanced query client with typed error handling, auto-retry, and logging
 *
 * Features:
 * - Typed error handling with ApplicationError
 * - Automatic retry for network errors with exponential backoff
 * - Global error logging with PII sanitization
 * - Context enrichment for debugging
 * - Integration with Phase 1 & 2 error handling
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: how long until a query is considered stale
      staleTime: 1000 * 60 * 5, // 5 minutes

      // Cache time: how long to keep unused data in cache — extended for
      // offline support, see OFFLINE_GC_TIME_MS for why it isn't longer.
      gcTime: OFFLINE_GC_TIME_MS,

      /**
       * Enhanced retry logic with typed error handling
       * - Offline errors: No retry (preserve cached data)
       * - Network errors: Retry up to 3 times with exponential backoff
       * - Auth errors (401, 403): No retry (handled by auth flow)
       * - Validation errors (400): No retry (client-side fix needed)
       * - Server errors (500+): Retry up to 2 times
       */
      retry: (failureCount, error: unknown) => {
        // Don't retry offline errors - keep using cached data
        const offlineErr = error as { code?: string };
        if (offlineErr?.code === 'OFFLINE') {
          return false;
        }

        // Check if it's an ApplicationError
        if (isApplicationError(error)) {
          // Don't retry authentication/authorization errors
          if (
            error.category === ErrorCategory.AUTHENTICATION ||
            error.category === ErrorCategory.AUTHORIZATION
          ) {
            return false;
          }

          // Don't retry validation errors (client-side issues)
          if (error.category === ErrorCategory.VALIDATION) {
            return false;
          }

          // Don't retry business logic errors (intentional rejections)
          if (error.category === ErrorCategory.BUSINESS_LOGIC) {
            return false;
          }

          // Retry network errors up to 3 times (but not if we know we're offline)
          if (error.category === ErrorCategory.NETWORK && failureCount < 3) {
            // Don't retry if we've detected we're offline (VPN scenario)
            if (!checkIsOnline()) {
              return false;
            }
            return true;
          }

          // Retry runtime errors up to 2 times
          if (error.category === ErrorCategory.RUNTIME && failureCount < 2) {
            return true;
          }

          return false;
        }

        // Fallback: Check HTTP status if available
        const err = error as { status?: number };
        if (err?.status) {
          // Don't retry 4xx errors (client errors)
          if (err.status >= 400 && err.status < 500) {
            return false;
          }
          // Retry 5xx errors up to 2 times
          if (err.status >= 500 && failureCount < 2) {
            return true;
          }
        }

        // Default: retry up to 2 times for unknown errors (but not if offline)
        if (!checkIsOnline()) {
          return false;
        }
        return failureCount < 2;
      },

      /**
       * Retry delay with exponential backoff
       * Matches RetryManager strategy: 1s, 2s, 4s, 8s (max 30s)
       */
      retryDelay: attemptIndex => RetryManager.exponentialBackoff(attemptIndex),

      // Refetch on window focus in production only
      refetchOnWindowFocus: import.meta.env.PROD,

      // Always refetch on reconnect to get fresh data
      refetchOnReconnect: 'always',

      // CRITICAL for offline support: use cached data when offline
      // 'offlineFirst' means: use cache when offline, fetch when online
      networkMode: 'offlineFirst',
    },
    mutations: {
      /**
       * Conservative retry for mutations
       * Only retry network timeouts (mutations have side effects)
       */
      retry: (failureCount, error: unknown) => {
        // Only retry if it's a network timeout AND we haven't retried yet
        if (isApplicationError(error)) {
          return (
            error.category === ErrorCategory.NETWORK &&
            error.code === 'NETWORK_TIMEOUT' &&
            failureCount < 1
          );
        }
        return false;
      },

      // Network mode
      networkMode: 'online',
    },
  },
});

/**
 * Subscribe to query/mutation cache events to detect network errors
 *
 * IMPORTANT: We only use these events to detect OFFLINE state (on network errors).
 * We do NOT mark online on success because:
 * - Queries can return cached data without making network requests (offlineFirst mode)
 * - Mutations can succeed via optimistic updates without network
 *
 * Online detection is handled by:
 * - checkActualConnectivity() which pings /health endpoint
 * - Periodic checks in useNetworkStatus hook
 */
if (typeof window !== 'undefined') {
  const queryCache = queryClient.getQueryCache();

  queryCache.subscribe(event => {
    // When a query fails with a network error, mark as offline
    if (event.type === 'updated' && event.action.type === 'error') {
      const error = event.action.error;
      if (isNetworkError(error)) {
        markOffline();
      }
    }
  });

  const mutationCache = queryClient.getMutationCache();

  mutationCache.subscribe(event => {
    // When a mutation fails with a network error, mark as offline
    if (event.type === 'updated' && event.action.type === 'error') {
      const error = event.action.error;
      if (isNetworkError(error)) {
        markOffline();
      }
    }
  });
}

// Helper to invalidate queries with proper typing
export const invalidateQueries = (keys: readonly unknown[]) => {
  return queryClient.invalidateQueries({ queryKey: keys });
};

// Helper to prefetch queries
export const prefetchQuery = async <T>(
  key: readonly unknown[],
  fetcher: () => Promise<T>,
  staleTime?: number
) => {
  return queryClient.prefetchQuery({
    queryKey: key,
    queryFn: fetcher,
    staleTime: staleTime || 1000 * 60 * 5, // Default 5 minutes
  });
};

// Helper to get cached data
export const getCachedData = <T>(key: readonly unknown[]): T | undefined => {
  return queryClient.getQueryData<T>(key);
};

// Helper to set cached data
export const setCachedData = <T>(key: readonly unknown[], data: T) => {
  queryClient.setQueryData(key, data);
};

/**
 * Base persister for React Query cache
 */
const basePersister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  key: 'splitfiesta-query-cache',
  // Throttle writes to localStorage to avoid performance issues
  throttleTime: 1000,
});

/**
 * Track restore state to prevent race condition
 * - During restore, React Query initializes with 0 queries
 * - Without this guard, persistClient would save 0 queries, wiping the cache
 * - We only allow persisting after restore completes AND we have queries
 */
let isRestoreComplete = false;
let hasPersistedSinceRestore = false;

/**
 * Custom persister with race condition protection
 * Prevents overwriting cached data during the restore phase
 */
export const queryPersister = {
  persistClient: (client: PersistedClient) => {
    // Don't persist until restore is complete
    if (!isRestoreComplete) {
      return;
    }

    // Only persist when online - don't overwrite good cache with failed queries
    // Use our enhanced checkIsOnline() which handles VPN scenarios
    if (!checkIsOnline()) {
      return;
    }

    const queryCount = client.clientState.queries?.length || 0;

    // Prevent persisting empty state right after restore
    // This catches the case where queries are temporarily 0 during initialization
    if (!hasPersistedSinceRestore && queryCount === 0) {
      return;
    }

    // Once we persist with queries, allow future persists
    if (queryCount > 0) {
      hasPersistedSinceRestore = true;
    }

    return basePersister.persistClient(client);
  },
  restoreClient: async () => {
    const result = await basePersister.restoreClient();

    // Mark restore as complete - now persisting is allowed
    isRestoreComplete = true;

    return result;
  },
  removeClient: () => {
    return basePersister.removeClient();
  },
};
